import { Injectable, Injector, NgZone, OnDestroy } from "@angular/core";
import { GoogleAuthService } from "./google-api.service";

/** 1 hour of stable-online idle before auto logout. */
export const ONLINE_IDLE_MS = 60 * 60 * 1000;

/** Require continuous online this long before idle clock starts. */
export const STABLE_ONLINE_MS = 60 * 1000;

const ACTIVITY_THROTTLE_MS = 1_000;
const ACTIVITY_STORAGE_KEY = "cbta_idle_activity_at";
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll", "click"] as const;

/**
 * Frontend online-idle logout for flight / intermittent connectivity:
 * - Idle only after stable online (debounce)
 * - Pause on any offline blip; restart fresh when stable again
 * - Multi-tab activity sync via localStorage
 * - Uses safeSignOut (never clears local session if logout API fails)
 */
@Injectable({ providedIn: "root" })
export class OnlineIdleLogoutService implements OnDestroy {
	private started = false;
	private stable = false;
	private stableTimer: ReturnType<typeof setTimeout> | null = null;
	private idleTimer: ReturnType<typeof setTimeout> | null = null;
	private lastActivityAt = 0;
	private loggingOut = false;

	private readonly onOnline = () => this.handleOnline();
	private readonly onOffline = () => this.handleOffline();
	private readonly onActivity = () => this.noteActivity();
	private readonly onStorage = (event: StorageEvent) => this.handleStorage(event);

	constructor(
		private readonly injector: Injector,
		private readonly ngZone: NgZone,
	) {}

	start(): void {
		if (this.started) {
			return;
		}
		this.started = true;
		this.ngZone.runOutsideAngular(() => {
			window.addEventListener("online", this.onOnline);
			window.addEventListener("offline", this.onOffline);
			window.addEventListener("storage", this.onStorage);
			for (const evt of ACTIVITY_EVENTS) {
				window.addEventListener(evt, this.onActivity, { passive: true, capture: true });
			}
		});

		if (navigator.onLine) {
			this.beginStableOnlineCountdown();
		}
	}

	stop(): void {
		if (!this.started) {
			return;
		}
		this.started = false;
		this.clearStableTimer();
		this.clearIdleTimer();
		this.stable = false;
		window.removeEventListener("online", this.onOnline);
		window.removeEventListener("offline", this.onOffline);
		window.removeEventListener("storage", this.onStorage);
		for (const evt of ACTIVITY_EVENTS) {
			window.removeEventListener(evt, this.onActivity, true);
		}
	}

	ngOnDestroy(): void {
		this.stop();
	}

	/** @internal test helper */
	isWatchingIdle(): boolean {
		return this.stable && this.idleTimer != null;
	}

	/** @internal test helper */
	isStableOnline(): boolean {
		return this.stable;
	}

	private handleOnline(): void {
		if (!this.started) {
			return;
		}
		this.beginStableOnlineCountdown();
	}

	private handleOffline(): void {
		this.stable = false;
		this.clearStableTimer();
		this.clearIdleTimer();
	}

	private beginStableOnlineCountdown(): void {
		this.clearStableTimer();
		this.stable = false;
		this.clearIdleTimer();

		if (!navigator.onLine) {
			return;
		}

		this.stableTimer = setTimeout(() => {
			this.stableTimer = null;
			if (!navigator.onLine || !this.started) {
				return;
			}
			this.stable = true;
			this.lastActivityAt = Date.now();
			this.scheduleIdleTimeout();
		}, STABLE_ONLINE_MS);
	}

	private noteActivity(): void {
		if (!this.started || !this.stable || !navigator.onLine) {
			return;
		}
		const now = Date.now();
		if (now - this.lastActivityAt < ACTIVITY_THROTTLE_MS) {
			return;
		}
		this.lastActivityAt = now;
		try {
			localStorage.setItem(ACTIVITY_STORAGE_KEY, String(now));
		} catch {
			// ignore quota / private mode
		}
		this.scheduleIdleTimeout();
	}

	private handleStorage(event: StorageEvent): void {
		if (event.key !== ACTIVITY_STORAGE_KEY || event.newValue == null) {
			return;
		}
		const at = parseInt(event.newValue, 10);
		if (!Number.isFinite(at) || !this.started || !this.stable) {
			return;
		}
		this.lastActivityAt = at;
		this.scheduleIdleTimeout();
	}

	private scheduleIdleTimeout(): void {
		this.clearIdleTimer();
		if (!this.stable || !navigator.onLine || !this.started) {
			return;
		}
		const elapsed = Date.now() - this.lastActivityAt;
		const remaining = Math.max(0, ONLINE_IDLE_MS - elapsed);
		this.idleTimer = setTimeout(() => {
			this.idleTimer = null;
			void this.onIdleTimeout();
		}, remaining);
	}

	private async onIdleTimeout(): Promise<void> {
		if (this.loggingOut || !this.started) {
			return;
		}
		if (!navigator.onLine || !this.stable) {
			return;
		}
		this.loggingOut = true;
		try {
			const auth = this.injector.get(GoogleAuthService);
			const ok = await auth.safeSignOut();
			if (!ok) {
				// Logout API failed / blip — keep session; retry idle from now when still stable.
				this.lastActivityAt = Date.now();
				if (this.started && this.stable && navigator.onLine) {
					this.scheduleIdleTimeout();
				}
			} else {
				this.stop();
			}
		} finally {
			this.loggingOut = false;
		}
	}

	private clearStableTimer(): void {
		if (this.stableTimer != null) {
			clearTimeout(this.stableTimer);
			this.stableTimer = null;
		}
	}

	private clearIdleTimer(): void {
		if (this.idleTimer != null) {
			clearTimeout(this.idleTimer);
			this.idleTimer = null;
		}
	}
}
